'use client';

import { Anchor, FormField, FormHelp, Input, Label, Textarea, VisuallyHidden } from '@zillow/constellation-10';
import { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type { HpInquiryPrefillDto } from '@zg-rentals/ts-hotpads-api-client';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { isApplePrivateRelayEmail } from '@/src/lib/auth/email';
import { LEGAL_URLS } from '@/src/lib/legalUrls';
import { focusFirstInvalidField } from '@/src/lib/forms/focusFirstInvalidField';
import { GatedButton } from '@/src/components/ui/GatedButton';
import type { ContactFormInputErrors, ContactFormInputValues, ContactFormMode, ContactFormServerErrors } from './types';
import * as styles from './contactForm.styles';

type ListingLike = PropertyDetailListing;

type ContactFormProps = {
  listing: ListingLike | null;
  onSubmit: (values: ContactFormInputValues) => void;
  isLoading: boolean;
  serverErrors: ContactFormServerErrors;
  inquiryPrefill?: HpInquiryPrefillDto;
  user?: {
    loggedIn?: boolean;
    info?: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    };
  };
  mode?: ContactFormMode;
  /**
   * Namespaces all element ids on this form (`<idPrefix>-form`, `<idPrefix>-userMessage`, etc.)
   * so multiple ContactForm instances on the same page (e.g. modal + inline) don't collide.
   * Defaults to "halo-contact" for backwards compatibility with the modal flow.
   */
  idPrefix?: string;
};

/**
 * Digits only, with a US country code dropped before the number is truncated to ten.
 *
 * Autofill and pasted numbers routinely arrive as +1XXXXXXXXXX. Slicing to ten without
 * dropping the leading 1 first promotes the country code to the area code and discards the
 * last real digit — the result still formats cleanly and still passes the ten-digit check,
 * so a wrong, unreachable number reaches the property manager with nothing to notice.
 */
function digitsOnly(raw: string | null | undefined): string {
  const digits = raw?.replace(/\D/g, '') ?? '';
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return national.slice(0, 10);
}

function formatPhoneNumber(raw: string): string {
  const digits = digitsOnly(raw);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function defaultMessageFor(
  mode: ContactFormMode,
  listing: ListingLike | null,
  t: ReturnType<typeof useTranslations<'contactForm'>>,
): string {
  if (mode === 'requestToApply') {
    return t('defaultMessageApply');
  }
  if (mode === 'tour') {
    return t('defaultMessageTour');
  }
  if (isWaitlistedListing(listing)) {
    return t('defaultMessageWaitlist');
  }
  return listing?.contact?.defaultInquiryMessage ?? '';
}

/**
 * Phase 5 ContactForm — a UI-only shell collecting name/email/phone/
 * message with client-side validation. The legacy HALO form also handled
 * income-restriction choice, mailcheck typo suggestions, and floor-plan
 * bed/bath selection; those surface in Phase 7 polish when the dependent
 * utilities port over.
 */
export const ContactForm = ({
  listing,
  onSubmit,
  isLoading,
  serverErrors,
  inquiryPrefill,
  user,
  mode = 'contact',
  idPrefix = 'halo-contact',
}: ContactFormProps) => {
  const formId = `${idPrefix}-form`;
  const messageId = `${idPrefix}-userMessage`;
  const messageLabelId = `${messageId}-label`;
  const nameId = `${idPrefix}-userName`;
  const nameLabelId = `${nameId}-label`;
  const emailId = `${idPrefix}-userEmail`;
  const emailLabelId = `${emailId}-label`;
  const phoneId = `${idPrefix}-userPhoneNumber`;
  const phoneLabelId = `${phoneId}-label`;
  const t = useTranslations('contactForm');
  const userInfoName =
    user?.info?.firstName && user?.info?.lastName ? `${user.info.firstName} ${user.info.lastName}` : '';
  const isLoggedIn = user?.loggedIn ?? false;

  const submitLabel =
    mode === 'requestToApply'
      ? t('submitApply')
      : mode === 'tour'
        ? t('submitTour')
        : isWaitlistedListing(listing)
          ? t('submitWaitlist')
          : t('submitContact');
  const defaultMessage = defaultMessageFor(mode, listing, t);

  const [inputValues, setInputValues] = useState<ContactFormInputValues>({
    userMessage: inquiryPrefill?.message ?? defaultMessage,
    userEmail: inquiryPrefill?.email ?? '',
    userName: inquiryPrefill?.name ?? userInfoName,
    userPhoneNumber: digitsOnly(inquiryPrefill?.phone),
    numBeds: '',
    numBaths: '',
    moveDate: '',
    lowIncomeMessage: null,
  });

  const [inputErrors, setInputErrors] = useState<ContactFormInputErrors>({
    userMessage: null,
    userEmail: null,
    userName: null,
    userPhoneNumber: null,
    numBeds: null,
    numBaths: null,
    moveDate: null,
  });

  // The renter owns a field the moment they type in it. Emptiness cannot stand in for
  // "untouched" — a field they cleared looks identical to one that was never filled.
  const touchedFieldsRef = useRef(new Set<keyof ContactFormInputValues>());

  const { contact } = listing ?? {};

  // The prefill endpoint owns every field the renter has not typed into, so a value that
  // resettles later replaces what is shown rather than only filling a blank. Keeping the
  // current value when the incoming one is empty is what leaves a first-time inquirer's
  // just-submitted details in place while the endpoint has no data for them yet.
  useEffect(() => {
    if (!inquiryPrefill) return;

    const applyPrefill = (field: keyof ContactFormInputValues, current: string, incoming: string) =>
      touchedFieldsRef.current.has(field) ? current : incoming || current;

    setInputValues((prev) => ({
      ...prev,
      userMessage: applyPrefill('userMessage', prev.userMessage, inquiryPrefill.message ?? ''),
      userEmail: applyPrefill('userEmail', prev.userEmail, inquiryPrefill.email ?? ''),
      userName: applyPrefill('userName', prev.userName, inquiryPrefill.name || userInfoName),
      userPhoneNumber: applyPrefill('userPhoneNumber', prev.userPhoneNumber, digitsOnly(inquiryPrefill.phone)),
    }));
  }, [userInfoName, inquiryPrefill]);

  useEffect(() => {
    if (!isLoggedIn || !userInfoName) return;
    setInputValues((prev) =>
      prev.userName || touchedFieldsRef.current.has('userName') ? prev : { ...prev, userName: userInfoName },
    );
  }, [isLoggedIn, userInfoName]);

  // A logged-in renter's email is locked to their account email and auto-filled (matches
  // legacy/HALO; every account, including freepass, has one). Otherwise it's the editable
  // guest/cached value. Deriving it (rather than syncing state in an effect) keeps the displayed
  // and submitted email consistent on the render auth resolves, and falls back to the editable
  // value the moment the user logs out.
  const effectiveEmail = isLoggedIn ? (user?.info?.email ?? '') : inputValues.userEmail;
  // Apple "Hide My Email" relay addresses can't receive property manager replies, so
  // contacting with one is blocked (matches legacy). Logged-in users can't edit the
  // locked email field, so they also get a note pointing at account settings.
  const hasMaskedEmail = isApplePrivateRelayEmail(effectiveEmail);
  const showPrivateRelayNote = hasMaskedEmail && isLoggedIn;

  // Which fields are mandatory is per-listing, so the submit gate and the validator
  // below must read the same flags or they will disagree about what "complete" means.
  const requires = useMemo(
    () => ({
      userMessage: contact?.requiresMessage !== false,
      userName: contact?.requiresName !== false,
      userEmail: contact?.requiresEmail !== false,
      userPhoneNumber: !!contact?.requiresPhone,
    }),
    [contact?.requiresEmail, contact?.requiresMessage, contact?.requiresName, contact?.requiresPhone],
  );
  // Gate on emptiness only. Format rules (full name, email shape, 10-digit phone) stay
  // in handleSubmit, which runs once the button enables and focuses whatever failed.
  const hasRequiredValues =
    (!requires.userMessage || !!inputValues.userMessage.trim()) &&
    (!requires.userName || !!inputValues.userName.trim()) &&
    (!requires.userEmail || !!effectiveEmail.trim()) &&
    (!requires.userPhoneNumber || !!inputValues.userPhoneNumber.trim());

  const handleFieldChange = useCallback(
    (field: keyof ContactFormInputValues) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      touchedFieldsRef.current.add(field);
      setInputValues((prev) => ({ ...prev, [field]: e.target.value }));
      if (field in inputErrors) {
        setInputErrors((prev) => ({ ...prev, [field]: null }));
      }
    },
    [inputErrors],
  );

  const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    touchedFieldsRef.current.add('userPhoneNumber');
    const raw = digitsOnly(e.target.value);
    setInputValues((prev) => ({ ...prev, userPhoneNumber: raw }));
    setInputErrors((prev) => ({ ...prev, userPhoneNumber: null }));
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      const errors: ContactFormInputErrors = {
        userMessage: null,
        userEmail: null,
        userName: null,
        userPhoneNumber: null,
        numBeds: null,
        numBaths: null,
        moveDate: null,
      };

      if (requires.userName) {
        const trimmedName = inputValues.userName.trim();
        if (!trimmedName) {
          errors.userName = t('errorName');
        } else if (!/\s/.test(trimmedName)) {
          errors.userName = t('errorNameFull');
        }
      }
      if (hasMaskedEmail) {
        errors.userEmail = t('errorEmailMasked');
      } else if (requires.userEmail) {
        if (!effectiveEmail.trim()) {
          errors.userEmail = t('errorEmailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(effectiveEmail)) {
          errors.userEmail = t('errorEmailInvalid');
        }
      }
      if (requires.userPhoneNumber && !inputValues.userPhoneNumber.trim()) {
        errors.userPhoneNumber = t('errorPhoneRequired');
      } else if (inputValues.userPhoneNumber && inputValues.userPhoneNumber.replace(/\D/g, '').length !== 10) {
        errors.userPhoneNumber = t('errorPhoneInvalid');
      }
      if (requires.userMessage && !inputValues.userMessage.trim()) {
        errors.userMessage = t('errorMessage');
      }

      setInputErrors(errors);
      if (Object.values(errors).some(Boolean)) {
        focusFirstInvalidField([
          errors.userMessage && messageId,
          errors.userName && nameId,
          errors.userEmail && emailId,
          errors.userPhoneNumber && phoneId,
        ]);
        return;
      }

      onSubmit({ ...inputValues, userEmail: effectiveEmail });
    },
    [effectiveEmail, emailId, hasMaskedEmail, inputValues, messageId, nameId, onSubmit, phoneId, requires, t],
  );

  return (
    <form id={formId} onSubmit={handleSubmit} className={styles.formContainer} noValidate>
      <FormField
        controlId={messageId}
        label={<Label id={messageLabelId}>{t('fieldMessage')}</Label>}
        required={requires.userMessage}
        error={!!inputErrors.userMessage}
        description={inputErrors.userMessage ? <FormHelp role="alert">{inputErrors.userMessage}</FormHelp> : undefined}
        control={
          <Textarea
            data-testid="textarea-contact-message"
            value={inputValues.userMessage}
            onChange={handleFieldChange('userMessage')}
            maxLength={750}
            rows={5}
          />
        }
      />

      <FormField
        controlId={nameId}
        label={<Label id={nameLabelId}>{t('fieldName')}</Label>}
        required={requires.userName}
        error={!!inputErrors.userName}
        description={inputErrors.userName ? <FormHelp role="alert">{inputErrors.userName}</FormHelp> : undefined}
        control={
          <Input
            data-testid="input-contact-name"
            type="text"
            value={inputValues.userName}
            onChange={handleFieldChange('userName')}
            maxLength={50}
            autoComplete="name"
          />
        }
      />

      <div className={styles.emailPhoneRow}>
        <FormField
          controlId={emailId}
          label={<Label id={emailLabelId}>{t('fieldEmail')}</Label>}
          required={requires.userEmail}
          error={!!(inputErrors.userEmail || serverErrors.emailError)}
          description={
            // A single FormHelp carries the FormField descriptionId that the input's
            // aria-describedby points at, so error and note must render inside it.
            inputErrors.userEmail || serverErrors.emailError || showPrivateRelayNote ? (
              <FormHelp>
                {(inputErrors.userEmail || serverErrors.emailError) && (
                  <span role="alert">{inputErrors.userEmail || serverErrors.emailError}</span>
                )}
                {showPrivateRelayNote && (
                  <span className={styles.privateRelayNote} role="note">
                    {t('appleRelayNoteLead')}{' '}
                    <Link href="/user/edit" className={styles.privateRelayNoteLink}>
                      {t('appleRelayNoteLink')}
                    </Link>
                    .
                  </span>
                )}
              </FormHelp>
            ) : undefined
          }
          control={
            <Input
              data-testid="input-contact-email"
              type="email"
              value={effectiveEmail}
              onChange={handleFieldChange('userEmail')}
              readOnly={isLoggedIn}
              maxLength={50}
              placeholder={t('emailPlaceholder')}
              autoComplete="email"
            />
          }
        />

        <FormField
          controlId={phoneId}
          label={<Label id={phoneLabelId}>{t('fieldPhone')}</Label>}
          required={requires.userPhoneNumber}
          error={!!(inputErrors.userPhoneNumber || serverErrors.phoneError)}
          description={
            inputErrors.userPhoneNumber || serverErrors.phoneError ? (
              <FormHelp role="alert">{inputErrors.userPhoneNumber || serverErrors.phoneError}</FormHelp>
            ) : undefined
          }
          control={
            <Input
              data-testid="input-contact-phone"
              type="tel"
              value={formatPhoneNumber(inputValues.userPhoneNumber)}
              onChange={handlePhoneChange}
              maxLength={14}
              placeholder={t('phonePlaceholder')}
              // `tel` asks the browser for the full international number, so Chrome supplies
              // a +1 that this field then has to strip. `tel-national` asks for the number
              // this field actually renders.
              autoComplete="tel-national"
            />
          }
        />
      </div>

      {serverErrors.formError && (
        <div className={styles.formError} role="alert">
          {serverErrors.formError}
        </div>
      )}

      {/* Always rendered, empty included: a live region that appears at the same moment its
          text does is not reliably announced. */}
      <VisuallyHidden role="status" aria-live="polite">
        {isLoading ? t('sending') : ''}
      </VisuallyHidden>

      <div className={styles.submitRow}>
        <GatedButton
          data-testid="button-contact-submit"
          tone="brand"
          emphasis="filled"
          type="submit"
          size="lg"
          fluid
          disabled={showPrivateRelayNote || !hasRequiredValues}
          loading={isLoading}
          // `showPrivateRelayNote` already renders its own visible explanation.
          gateReason={!isLoading && !showPrivateRelayNote && !hasRequiredValues && t('gateHint')}
        >
          {submitLabel}
        </GatedButton>
        <p className={styles.legalFootnote}>
          {t('legalLead')}{' '}
          <Anchor href={LEGAL_URLS.termsOfUse} target="_blank" rel="noopener noreferrer">
            {t('legalTerms')}
          </Anchor>
          {t('legalMid')}{' '}
          <Anchor href={LEGAL_URLS.privacyNotice} target="_blank" rel="noopener noreferrer">
            {t('legalPrivacy')}
          </Anchor>{' '}
          {t('legalTrail', { action: submitLabel })}
        </p>
      </div>
    </form>
  );
};
